import type {
  Client,
  ClientsPayload,
  ClientsResponse,
  ClientItemPayload,
  ClientItemResponse,
  CreateClientItemPayload,
  AddBonusPayload,
} from '@/types/clients'
import type { DefaultError } from '@/types/httpError'
import type { DefaultDeletePayload, DeleteSuccessResponse, ItemPayload } from '@/types/main'
import {
  getClientsCall,
  getClientItemCall,
  editClientItemCall,
  getClientsForSelectCall,
  createClientItemCall,
  sendGeneratedPasswordCall,
  deleteClientItemCall,
  addBonusToClientItemCall,
} from '@/api/clients'
import { useApiCall } from '@/composables/useApiCall'
import { defineStore } from 'pinia'
import { computed, reactive, ref, watch } from 'vue'
import { useFilterStore } from '@/store/filter'
import { useRoute } from 'vue-router'

export const useClientsStore = defineStore('clients', () => {
  const clients = ref<Client[]>([])
  const clientsForSelect = ref<Client[]>([])
  const openedClient = ref<Client | null>(null)
  const filterStore = useFilterStore()
  const route = useRoute()
  const metaData = reactive({
    total: 0,
    totalPages: 0,
  })
  const filterFields = reactive({
    page: 1,
    perPage: 15,
  }) as ClientsPayload

  const statuses = {
    Базовый: 'info',
    Бонусный: 'success',
    Продвинутый: 'secondary',
  }

  const getStatus = (status: string) => {
    return statuses[status as keyof typeof statuses]
  }

  const {
    data: clientsData,
    executeApiCall: clientsAction,
    isLoading: clientsLoading,
    error: clientsError,
  } = useApiCall<ClientsResponse, DefaultError, ClientsPayload>(getClientsCall, true)

  const {
    data: clientsForSelectData,
    executeApiCall: clientsForSelectAction,
    error: clientsForSelectError,
    isLoading: clientsForSelectLoading,
  } = useApiCall<Client[], DefaultError, ClientsPayload>(getClientsForSelectCall, true)

  const isClientsLoading = computed(() => clientsLoading.value)
  const isClientSelectLoading = computed(() => clientsForSelectLoading.value)
  const changePage = async (page: number) => {
    if (filterFields.page === page) {
      return
    }
    filterFields.page = page
    await getClients()
  }

  const clearFilter = async () => {
    clearQuery()
    await applyFilter(filterFields)
  }

  const applyFilter = async (filters: ClientsPayload) => {
    Object.keys(filters).forEach(key => {
      if (filters[key as keyof ClientsPayload] || filters[key as keyof ClientsPayload] === false) {
        filterFields[key as keyof ClientsPayload] = filters[key as keyof ClientsPayload]
      } else {
        filterFields[key as keyof ClientsPayload] = ''
      }
    })
    filterFields.page = 1
    await getClients()
  }

  watch(
    () => filterStore.isSend,
    async () => {
      if (filterStore.isSend && route.name === 'clients') {
        await applyFilter({
          search: filterStore.filters.search,
        })
        filterStore.isSend = false
      }
    },
  )

  const getClients = async () => {
    try {
      const filter = {} as ClientsPayload
      Object.keys(filterFields).forEach(key => {
        if (
          filterFields[key as keyof ClientsPayload]
          || filterFields[key as keyof ClientsPayload] === false
        ) {
          filter[key as keyof ClientsPayload] = filterFields[key as keyof ClientsPayload]
        }
      })
      await clientsAction(filter)
      if (clientsData.value) {
        clients.value = [...clientsData.value.data]
        metaData.total = clientsData.value.pagination.total
        metaData.totalPages = clientsData.value.pagination.totalPages
      }
    } catch {
      console.error(clientsError.value)
    }
  }

  const getClientsWithSearch = async (search: string): Promise<Client[]> => {
    try {
      await clientsAction({ search, perPage: 100 })
      if (clientsData.value) {
        return clientsData.value.data
      }
    } catch {
      console.error(clientsError.value)
    }
    return []
  }

  const clearQuery = () => {
    metaData.totalPages = 0
    metaData.total = 0
    filterFields.page = 1
    filterFields.perPage = 15
    Object.keys(filterFields).forEach(
      n => ['page', 'perPage'].includes(n) || delete filterFields[n],
    )
  }

  const getClientsForSelect = async () => {
    try {
      await clientsForSelectAction()
      if (clientsForSelectData.value) {
        clientsForSelect.value = clientsForSelectData.value
      }
    } catch {
      throw clientsForSelectError.value?.data
    }
  }

  const {
    data: clientItemData,
    executeApiCall: getClientItemAction,
    error: clientItemError,
  } = useApiCall<ClientItemResponse, DefaultError, ItemPayload>(getClientItemCall, true)

  const getClientItem = async (id: number) => {
    try {
      await getClientItemAction({ id })
      if (clientItemData.value) {
        openedClient.value = clientItemData.value.data
      }
    } catch {
      throw clientItemError.value?.data
    }
  }

  const {
    isLoading: isEditClientItemLoading,
    executeApiCall: editClientItemAction,
    error: editClientItemError,
  } = useApiCall<ClientItemResponse, DefaultError, ClientItemPayload>(editClientItemCall, true)

  const editClientItem = async (params: ClientItemPayload) => {
    try {
      await editClientItemAction(params)
    } catch {
      throw editClientItemError.value?.data
    }
  }

  const {
    isLoading: isAddBonusLoading,
    executeApiCall: addBonusAction,
    error: addBonusError,
  } = useApiCall<ClientItemResponse, DefaultError, AddBonusPayload>(addBonusToClientItemCall, true)

  const addBonusToClientItem = async (params: AddBonusPayload) => {
    try {
      await addBonusAction(params)
    } catch {
      throw addBonusError.value?.data
    }
  }

  const {
    data: createdClient,
    executeApiCall: createClientItemAction,
    error: createClientItemError,
  } = useApiCall<Client, DefaultError, CreateClientItemPayload>(createClientItemCall, true)

  const createClientItem = async (body: CreateClientItemPayload): Promise<Client | null> => {
    try {
      await createClientItemAction(body)
      if (createdClient.value) {
        return createdClient.value
      }
    } catch (error) {
      if (createClientItemError.value) {
        throw createClientItemError.value?.data
      }
    }
    return null
  }

  const { executeApiCall: sendPasswordAction, error: sendPasswordError } = useApiCall<
    DeleteSuccessResponse,
    DefaultError,
    ItemPayload
  >(sendGeneratedPasswordCall, true)

  const sendGeneratedPassword = async (id: number) => {
    try {
      await sendPasswordAction({ id })
    } catch (error) {
      if (sendPasswordError.value) {
        throw sendPasswordError.value?.data
      }
    }
  }

  const { executeApiCall: deleteClientItemAction, error: deleteClientItemError } = useApiCall<
    DeleteSuccessResponse,
    DefaultError,
    DefaultDeletePayload
  >(deleteClientItemCall, true)

  const deleteClientItem = async (id: number) => {
    try {
      await deleteClientItemAction({ id })
    } catch (error) {
      if (deleteClientItemError.value) {
        throw deleteClientItemError.value?.data
      }
    }
  }

  const $reset = () => {
    clearQuery()
    clients.value = []
    clientsForSelect.value = []
    openedClient.value = null
  }

  return {
    clients,
    clientsForSelect,
    openedClient,
    isClientsLoading,
    metaData,
    filterFields,
    changePage,
    clearQuery,
    applyFilter,
    clearFilter,
    getClients,
    getClientsWithSearch,
    getClientItem,
    getStatus,
    isEditClientItemLoading,
    editClientItem,
    isAddBonusLoading,
    addBonusToClientItem,
    getClientsForSelect,
    createClientItem,
    sendGeneratedPassword,
    deleteClientItem,
    $reset,
    isClientSelectLoading,
  }
})
